import React, { useEffect, useState, useCallback } from "react";
import DI from "../../utility/DI";
import {
  Box,
  Typography,
  Paper,
  Button,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  CircularProgress,
  Drawer,
  IconButton,
  Tooltip,
  Avatar,
  Stack,
  Card,
  CardContent,
  InputAdornment,
  Grid,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";
import AddCardIcon from "@mui/icons-material/AddCard";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import CloseIcon from "@mui/icons-material/Close";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import TrendingDownIcon from "@mui/icons-material/TrendingDown";
import PaymentsIcon from "@mui/icons-material/Payments";
import ShoppingBagIcon from "@mui/icons-material/ShoppingBag";
import RefreshIcon from "@mui/icons-material/Refresh";
import PersonRemoveIcon from "@mui/icons-material/PersonRemove";
import { toTitleCase } from "../../utility/tools";

const QUICK_AMOUNTS = [200, 500, 1000, 2000, 5000];
const QUICK_NOTES = ["Monthly Allowance", "Groceries Advance", "Pocket Money", "Emergency Cash"];

const WalletPanel = (props) => {
  const {
    currentHome,
    di: { GET, POST, urls, session, error, success },
    embedded = false,
    onRemoveMember,
    homeCreatorId,
    refreshTrigger,
  } = props;

  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";

  const [wallets, setWallets] = useState([]);
  const [isOwner, setIsOwner] = useState(false);
  const [loading, setLoading] = useState(false);

  // Recharge dialog
  const [rechargeOpen, setRechargeOpen] = useState(false);
  const [rechargeUserId, setRechargeUserId] = useState("");
  const [rechargeAmount, setRechargeAmount] = useState("");
  const [rechargeNote, setRechargeNote] = useState("");
  const [rechargeLoading, setRechargeLoading] = useState(false);

  // Ledger drawer
  const [ledgerOpen, setLedgerOpen] = useState(false);
  const [ledgerMember, setLedgerMember] = useState(null);
  const [ledgerData, setLedgerData] = useState([]);
  const [ledgerBalance, setLedgerBalance] = useState(0);
  const [ledgerLoading, setLedgerLoading] = useState(false);

  const fetchWallets = useCallback(async () => {
    if (!currentHome) return;
    setLoading(true);
    try {
      const res = await GET(urls.get.get_wallet, { home_id: currentHome });
      if (res?.success) {
        setWallets(res.data || []);
        setIsOwner(Boolean(res.is_owner));
      } else {
        error(res?.message ?? "Failed to load wallets");
      }
    } catch {
      error("Failed to load wallets");
    }
    setLoading(false);
  }, [currentHome, GET, urls.get.get_wallet, error]);

  useEffect(() => {
    if (currentHome) fetchWallets();
  }, [currentHome, refreshTrigger]);

  const handleRecharge = async () => {
    if (!rechargeUserId) return error("Select a member");
    const amt = parseFloat(rechargeAmount);
    if (!amt || amt <= 0) return error("Enter a valid positive amount");

    setRechargeLoading(true);
    try {
      const res = await POST(urls.post.recharge_wallet, {
        home_id: currentHome,
        user_id: rechargeUserId,
        amount: amt,
        note: rechargeNote || "Wallet recharge",
      });
      if (res?.success) {
        success(res.message);
        setRechargeOpen(false);
        setRechargeAmount("");
        setRechargeNote("");
        setRechargeUserId("");
        fetchWallets();
      } else {
        error(res?.message ?? "Recharge failed");
      }
    } catch {
      error("Recharge failed");
    }
    setRechargeLoading(false);
  };

  const openLedger = (member) => {
    setLedgerMember(member);
    setLedgerBalance(parseFloat(member?.balance || 0));
    setLedgerOpen(true);
    fetchLedger(member.user_id, member?.balance);
  };

  const fetchLedger = async (userId, fallbackBalance = 0) => {
    setLedgerLoading(true);
    try {
      const res = await GET(urls.get.get_wallet_ledger, {
        home_id: currentHome,
        user_id: userId,
      });
      if (res?.success) {
        setLedgerData(res.data || res.ledger || []);
        setLedgerBalance(
          res.balance !== undefined
            ? parseFloat(res.balance)
            : res.current_balance !== undefined
              ? parseFloat(res.current_balance)
              : parseFloat(fallbackBalance || 0)
        );
      } else {
        error(res?.message ?? "Failed to load ledger");
      }
    } catch {
      error("Failed to load ledger");
    }
    setLedgerLoading(false);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "";
    try {
      const d = new Date(dateStr);
      return d.toLocaleDateString("en-IN", {
        day: "numeric",
        month: "short",
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch {
      return dateStr;
    }
  };

  const totalRecharged = ledgerData
    .filter((e) => e.type === "recharge")
    .reduce((acc, curr) => acc + (parseFloat(curr.amount) || 0), 0);
  const totalSpent = ledgerData
    .filter((e) => e.type === "deduction")
    .reduce((acc, curr) => acc + (parseFloat(curr.amount) || 0), 0);

  if (!currentHome) return null;

  return (
    <Box
      sx={{
        mt: 1.5,
        mb: 1.5,
        background: isDark
          ? "rgba(30, 41, 59, 0.45)"
          : "linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(248, 250, 252, 0.95) 100%)",
      }}
    >
      {/* Dense Header bar */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 1,
          mb: 1.2,
          pb: 1,
          borderBottom: "1px solid",
          borderColor: isDark ? "rgba(51, 65, 85, 0.7)" : "rgba(226, 232, 240, 0.8)",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Box
            sx={{
              width: 28,
              height: 28,
              borderRadius: 1.8,
              background: "linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "#fff",
              boxShadow: "0 2px 6px rgba(99, 102, 241, 0.25)",
            }}
          >
            <AccountBalanceWalletIcon sx={{ fontSize: 16 }} />
          </Box>
          <Typography variant="subtitle2" sx={{ fontWeight: 800, color: "text.primary", fontSize: "0.85rem" }}>
            {isOwner ? "Home Members" : "My Home"}
          </Typography>
        </Box>

        <Stack direction="row" spacing={0.8} alignItems="center">
          <Tooltip title="Refresh Wallets">
            <IconButton
              size="small"
              onClick={fetchWallets}
              sx={{
                width: 26,
                height: 26,
                bgcolor: isDark ? "rgba(255, 255, 255, 0.06)" : "#f1f5f9",
                color: isDark ? "#94a3b8" : "#475569",
                "&:hover": { bgcolor: isDark ? "rgba(255, 255, 255, 0.12)" : "#e2e8f0" },
              }}
            >
              <RefreshIcon sx={{ fontSize: 14 }} />
            </IconButton>
          </Tooltip>
          {isOwner && (
            <Button
              variant="contained"
              size="small"
              startIcon={<AddCardIcon sx={{ fontSize: "14px !important" }} />}
              onClick={() => {
                setRechargeOpen(true);
                setRechargeUserId("");
              }}
              sx={{
                textTransform: "none",
                fontWeight: 700,
                fontSize: "0.72rem",
                borderRadius: 2,
                px: 1.2,
                py: 0.3,
                height: 26,
                background: "linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)",
                boxShadow: "0 2px 8px rgba(37, 99, 235, 0.2)",
                "&:hover": {
                  background: "linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)",
                },
              }}
            >
              Recharge
            </Button>
          )}
        </Stack>
      </Box>

      {/* Cards Container */}
      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
          <CircularProgress size={22} thickness={4} />
        </Box>
      ) : wallets.length === 0 ? (
        <Box
          sx={{
            textAlign: "center",
            py: 1.5,
            bgcolor: isDark ? "rgba(255, 255, 255, 0.03)" : "#f8fafc",
            borderRadius: 2,
            border: "1px dashed",
            borderColor: isDark ? "rgba(51, 65, 85, 0.7)" : "#cbd5e1",
          }}
        >
          <Typography variant="caption" sx={{ color: "text.secondary", fontWeight: 500 }}>
            No members enrolled in this home yet.
          </Typography>
        </Box>
      ) : (
        <Grid container spacing={1.2}>
          {wallets.map((w) => {
            const isSelf = String(w.user_id) === String(session?.id);
            const isHomeCreator = String(w.user_id) === String(homeCreatorId);
            const isNegative = w.balance < 0;
            const isPositive = w.balance > 0;
            const isZero = w.balance === 0;

            const cardBg = isDark
              ? isNegative
                ? "linear-gradient(145deg, rgba(30, 41, 59, 0.85) 0%, rgba(239, 68, 68, 0.08) 100%)"
                : isPositive
                  ? "linear-gradient(145deg, rgba(30, 41, 59, 0.85) 0%, rgba(34, 197, 94, 0.08) 100%)"
                  : "linear-gradient(145deg, rgba(30, 41, 59, 0.85) 0%, rgba(51, 65, 85, 0.35) 100%)"
              : isNegative
                ? "linear-gradient(145deg, #ffffff 0%, #fff8f8 100%)"
                : isPositive
                  ? "linear-gradient(145deg, #ffffff 0%, #f4fdf7 100%)"
                  : "linear-gradient(145deg, #ffffff 0%, #f8fafc 100%)";

            const borderColor = isDark
              ? isNegative
                ? "rgba(239, 68, 68, 0.35)"
                : isPositive
                  ? "rgba(34, 197, 94, 0.35)"
                  : "rgba(51, 65, 85, 0.7)"
              : isNegative
                ? "rgba(239, 68, 68, 0.3)"
                : isPositive
                  ? "rgba(34, 197, 94, 0.3)"
                  : "rgba(226, 232, 240, 0.9)";

            const accentColor = isNegative
              ? isDark ? "#f87171" : "#dc2626"
              : isPositive
                ? isDark ? "#4ade80" : "#16a34a"
                : isDark ? "#94a3b8" : "#475569";

            return (
              <Grid item xs={12} sm={6} md={4} key={w.user_id}>
                <Card
                  elevation={0}
                  sx={{
                    borderRadius: 2.5,
                    background: cardBg,
                    border: `1px solid ${borderColor}`,
                    position: "relative",
                    overflow: "hidden",
                    transition: "all 0.2s ease",
                    "&:hover": {
                      boxShadow: isNegative
                        ? "0 6px 14px -3px rgba(239, 68, 68, 0.12)"
                        : isPositive
                          ? "0 6px 14px -3px rgba(34, 197, 94, 0.12)"
                          : "0 6px 14px -3px rgba(0, 0, 0, 0.05)",
                      borderColor: isNegative ? "#ef4444" : isPositive ? "#22c55e" : "#94a3b8",
                    },
                  }}
                >
                  {/* Dense Status Top Strip */}
                  <Box
                    sx={{
                      height: 3,
                      width: "100%",
                      background: isNegative
                        ? "linear-gradient(90deg, #ef4444, #f87171)"
                        : isPositive
                          ? "linear-gradient(90deg, #22c55e, #4ade80)"
                          : "linear-gradient(90deg, #94a3b8, #cbd5e1)",
                    }}
                  />

                  <CardContent sx={{ p: 1.2, "&:last-child": { pb: 1.2 } }}>
                    {/* Member Details */}
                    <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", mb: 0.8, gap: 0.5 }}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 0.8, minWidth: 0, flex: 1 }}>
                        <Avatar
                          sx={{
                            width: 28,
                            height: 28,
                            fontWeight: 700,
                            fontSize: "0.75rem",
                            bgcolor: isDark
                              ? isNegative
                                ? "rgba(239, 68, 68, 0.2)"
                                : isPositive
                                  ? "rgba(34, 197, 94, 0.2)"
                                  : "rgba(255, 255, 255, 0.06)"
                              : isNegative
                                ? "#fee2e2"
                                : isPositive
                                  ? "#dcfce7"
                                  : "#f1f5f9",
                            color: accentColor,
                            border: `1px solid ${isDark
                              ? isNegative
                                ? "rgba(248, 113, 113, 0.3)"
                                : isPositive
                                  ? "rgba(74, 222, 128, 0.3)"
                                  : "rgba(148, 163, 184, 0.2)"
                              : isNegative
                                ? "#fca5a5"
                                : isPositive
                                  ? "#86efac"
                                  : "#cbd5e1"
                              }`,
                          }}
                        >
                          {(w.firstname?.[0] ?? "?").toUpperCase()}
                        </Avatar>
                        <Box sx={{ minWidth: 0, flex: 1 }}>
                          <Typography
                            variant="body2"
                            sx={{ fontWeight: 700, color: "text.primary", fontSize: "0.82rem", lineHeight: 1.2 }}
                            noWrap
                          >
                            {toTitleCase(w.firstname + " " + (w.lastname ?? ""))}
                          </Typography>
                          <Typography
                            variant="caption"
                            sx={{ color: "text.secondary", fontSize: "0.68rem", display: "block" }}
                            noWrap
                          >
                            #{w.user_id}{w.email ? ` • ${w.email}` : ""}
                          </Typography>
                        </Box>
                      </Box>

                      <Stack direction="row" spacing={0.4} alignItems="center" sx={{ flexShrink: 0 }}>
                        {/* Status Tag */}
                        <Chip
                          icon={
                            isNegative ? (
                              <TrendingDownIcon sx={{ "&&": { color: isDark ? "#f87171" : "#dc2626", fontSize: 13 } }} />
                            ) : isPositive ? (
                              <TrendingUpIcon sx={{ "&&": { color: isDark ? "#4ade80" : "#16a34a", fontSize: 13 } }} />
                            ) : undefined
                          }
                          label={isNegative ? "Deficit" : isPositive ? "Prepaid" : "Balanced"}
                          size="small"
                          sx={{
                            fontWeight: 700,
                            fontSize: "0.62rem",
                            borderRadius: 1.5,
                            px: 0.2,
                            height: 20,
                            bgcolor: isDark
                              ? isNegative
                                ? "rgba(239, 68, 68, 0.15)"
                                : isPositive
                                  ? "rgba(34, 197, 94, 0.15)"
                                  : "rgba(255, 255, 255, 0.05)"
                              : isNegative
                                ? "#fef2f2"
                                : isPositive
                                  ? "#f0fdf4"
                                  : "#f8fafc",
                            color: accentColor,
                            border: `1px solid ${isDark
                              ? isNegative
                                ? "rgba(248, 113, 113, 0.3)"
                                : isPositive
                                  ? "rgba(74, 222, 128, 0.3)"
                                  : "rgba(148, 163, 184, 0.2)"
                              : isNegative
                                ? "#fecaca"
                                : isPositive
                                  ? "#bbf7d0"
                                  : "#e2e8f0"
                              }`,
                          }}
                        />

                        {/* Remove Member Button (Owner only, cannot remove owner itself) */}
                        {isOwner && onRemoveMember && !isHomeCreator && (
                          <Tooltip title="Remove member from home">
                            <IconButton
                              size="small"
                              onClick={() => onRemoveMember(w.user_id)}
                              sx={{
                                width: 22,
                                height: 22,
                                color: "error.main",
                                p: 0.2,
                                "&:hover": {
                                  bgcolor: isDark ? "rgba(239, 68, 68, 0.18)" : "#fee2e2",
                                },
                              }}
                            >
                              <PersonRemoveIcon sx={{ fontSize: 15 }} />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Stack>
                    </Box>

                    {/* Dense Balance Bar */}
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        py: 0.5,
                        px: 1,
                        borderRadius: 1.8,
                        bgcolor: isNegative
                          ? "rgba(254, 242, 242, 0.7)"
                          : isPositive
                            ? "rgba(240, 253, 244, 0.7)"
                            : "rgba(248, 250, 252, 0.85)",
                        border: "1px solid",
                        borderColor: isNegative
                          ? "rgba(254, 202, 202, 0.6)"
                          : isPositive
                            ? "rgba(187, 247, 208, 0.6)"
                            : "rgba(241, 245, 249, 0.9)",
                        mb: 1,
                      }}
                    >
                      <Typography variant="caption" sx={{ color: "#64748b", fontWeight: 600, fontSize: "0.68rem" }}>
                        {isNegative ? "Deficit:" : isPositive ? "Prepaid:" : "Balance:"}
                      </Typography>
                      <Typography
                        variant="subtitle2"
                        sx={{
                          fontWeight: 900,
                          color: accentColor,
                          fontSize: "0.95rem",
                          fontFamily: "monospace",
                        }}
                      >
                        {isNegative ? "- ₹" : "₹"}
                        {Math.abs(parseFloat(w.balance)).toLocaleString("en-IN", {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        })}
                      </Typography>
                    </Box>

                    {/* Compact Action buttons */}
                    <Box sx={{ display: "flex", gap: 0.8 }}>
                      {/* Ledger / Statement Button */}
                      {isOwner ? (
                        <Button
                          fullWidth
                          size="small"
                          variant="outlined"
                          startIcon={<ReceiptLongIcon sx={{ fontSize: "14px !important" }} />}
                          onClick={() => openLedger(w)}
                          sx={{
                            textTransform: "none",
                            fontWeight: 700,
                            fontSize: "0.72rem",
                            height: 26,
                            borderRadius: 1.5,
                            borderColor: isDark ? "rgba(148, 163, 184, 0.3)" : "#cbd5e1",
                            color: isDark ? "#cbd5e1" : "#334155",
                            bgcolor: isDark ? "rgba(255, 255, 255, 0.04)" : "#ffffff",
                            "&:hover": {
                              bgcolor: isDark ? "rgba(255, 255, 255, 0.08)" : "#f1f5f9",
                              borderColor: isDark ? "rgba(148, 163, 184, 0.5)" : "#94a3b8",
                            },
                          }}
                        >
                          Ledger
                        </Button>
                      ) : isSelf ? (
                        <Button
                          fullWidth
                          size="small"
                          variant="contained"
                          startIcon={<ReceiptLongIcon sx={{ fontSize: "14px !important" }} />}
                          onClick={() => openLedger(w)}
                          sx={{
                            textTransform: "none",
                            fontWeight: 700,
                            fontSize: "0.72rem",
                            height: 26,
                            borderRadius: 1.5,
                            background: "linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)",
                            color: "#ffffff",
                            boxShadow: "0 2px 6px rgba(37, 99, 235, 0.2)",
                            "&:hover": {
                              background: "linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)",
                            },
                          }}
                        >
                          My Statement
                        </Button>
                      ) : (
                        <Button
                          fullWidth
                          size="small"
                          disabled
                          variant="outlined"
                          startIcon={<ReceiptLongIcon sx={{ fontSize: "14px !important" }} />}
                          sx={{
                            textTransform: "none",
                            fontWeight: 700,
                            fontSize: "0.72rem",
                            height: 26,
                            borderRadius: 1.5,
                            borderColor: isDark ? "rgba(51, 65, 85, 0.5) !important" : "#e2e8f0 !important",
                            color: isDark ? "rgba(148, 163, 184, 0.4) !important" : "#94a3b8 !important",
                            bgcolor: isDark ? "rgba(255, 255, 255, 0.02)" : "#f8fafc",
                          }}
                        >
                          Ledger
                        </Button>
                      )}

                      {/* Recharge Button (Owner Only) */}
                      {isOwner ? (
                        <Button
                          fullWidth
                          size="small"
                          variant="contained"
                          startIcon={<AddCardIcon sx={{ fontSize: "14px !important" }} />}
                          onClick={() => {
                            setRechargeUserId(String(w.user_id));
                            setRechargeOpen(true);
                          }}
                          sx={{
                            textTransform: "none",
                            fontWeight: 700,
                            fontSize: "0.72rem",
                            height: 26,
                            borderRadius: 1.5,
                            background: isNegative
                              ? "linear-gradient(135deg, #ef4444 0%, #dc2626 100%)"
                              : "linear-gradient(135deg, #10b981 0%, #059669 100%)",
                            boxShadow: isNegative
                              ? "0 2px 6px rgba(239, 68, 68, 0.2)"
                              : "0 2px 6px rgba(16, 185, 129, 0.2)",
                            "&:hover": {
                              background: isNegative
                                ? "linear-gradient(135deg, #dc2626 0%, #b91c1c 100%)"
                                : "linear-gradient(135deg, #059669 0%, #047857 100%)",
                            },
                          }}
                        >
                          Recharge
                        </Button>
                      ) : (
                        <Button
                          fullWidth
                          size="small"
                          disabled
                          variant="outlined"
                          startIcon={<AddCardIcon sx={{ fontSize: "14px !important" }} />}
                          sx={{
                            textTransform: "none",
                            fontWeight: 700,
                            fontSize: "0.72rem",
                            height: 26,
                            borderRadius: 1.5,
                            borderColor: isDark ? "rgba(51, 65, 85, 0.5) !important" : "#e2e8f0 !important",
                            color: isDark ? "rgba(148, 163, 184, 0.4) !important" : "#94a3b8 !important",
                            bgcolor: isDark ? "rgba(255, 255, 255, 0.02)" : "#f8fafc",
                          }}
                        >
                          Recharge
                        </Button>
                      )}
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      )}

      {/* ================= RECHARGE DIALOG ================= */}
      <Dialog
        open={rechargeOpen}
        onClose={() => setRechargeOpen(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 4,
            p: 1,
            boxShadow: "0 20px 40px rgba(0,0,0,0.15)",
          },
        }}
      >
        <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", pb: 1 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Box
              sx={{
                width: 34,
                height: 34,
                borderRadius: 2,
                bgcolor: "#dcfce7",
                color: "#16a34a",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <PaymentsIcon fontSize="small" />
            </Box>
            <Typography variant="h6" sx={{ fontWeight: 800 }}>
              Recharge Member
            </Typography>
          </Box>
          <IconButton size="small" onClick={() => setRechargeOpen(false)}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 2, pt: "14px !important" }}>
          <FormControl fullWidth size="small">
            <InputLabel>Select Member</InputLabel>
            <Select
              value={rechargeUserId}
              label="Select Member"
              onChange={(e) => setRechargeUserId(e.target.value)}
              sx={{ borderRadius: 2 }}
            >
              {wallets.map((w) => (
                <MenuItem key={w.user_id} value={String(w.user_id)}>
                  <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", width: "100%" }}>
                    <span>{toTitleCase(w.firstname + " " + (w.lastname ?? ""))}</span>
                    <Typography
                      component="span"
                      variant="caption"
                      sx={{
                        fontWeight: 700,
                        color: w.balance < 0 ? "#dc2626" : w.balance > 0 ? "#16a34a" : "#64748b",
                        bgcolor: w.balance < 0 ? "#fee2e2" : w.balance > 0 ? "#dcfce7" : "#f1f5f9",
                        px: 1,
                        py: 0.2,
                        borderRadius: 1.5,
                      }}
                    >
                      ₹{parseFloat(w.balance).toFixed(2)}
                    </Typography>
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Amount input */}
          <Box>
            <TextField
              label="Recharge Amount"
              type="number"
              size="small"
              value={rechargeAmount}
              onChange={(e) => setRechargeAmount(e.target.value)}
              InputProps={{
                startAdornment: <InputAdornment position="start">₹</InputAdornment>,
              }}
              inputProps={{ min: 1 }}
              fullWidth
              sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
            />

            {/* Quick Amount Chips */}
            <Stack direction="row" spacing={0.8} sx={{ mt: 1, flexWrap: "wrap", gap: 0.6 }}>
              {QUICK_AMOUNTS.map((amt) => (
                <Chip
                  key={amt}
                  label={`+₹${amt}`}
                  size="small"
                  onClick={() => setRechargeAmount(String(amt))}
                  sx={{
                    fontWeight: 700,
                    borderRadius: 1.5,
                    cursor: "pointer",
                    bgcolor: rechargeAmount === String(amt) ? "#2563eb" : "#f1f5f9",
                    color: rechargeAmount === String(amt) ? "#fff" : "#334155",
                    "&:hover": { bgcolor: rechargeAmount === String(amt) ? "#1d4ed8" : "#e2e8f0" },
                  }}
                />
              ))}
            </Stack>
          </Box>

          {/* Note Input */}
          <Box>
            <TextField
              label="Purpose / Note (optional)"
              size="small"
              value={rechargeNote}
              onChange={(e) => setRechargeNote(e.target.value)}
              placeholder="e.g. Monthly Pocket Money"
              fullWidth
              sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
            />

            {/* Quick Note Suggestions */}
            <Stack direction="row" spacing={0.6} sx={{ mt: 0.8, flexWrap: "wrap", gap: 0.5 }}>
              {QUICK_NOTES.map((n) => (
                <Chip
                  key={n}
                  label={n}
                  size="small"
                  variant="outlined"
                  onClick={() => setRechargeNote(n)}
                  sx={{
                    fontSize: "0.68rem",
                    borderRadius: 1.5,
                    cursor: "pointer",
                    borderColor: rechargeNote === n ? "#2563eb" : "#cbd5e1",
                    color: rechargeNote === n ? "#2563eb" : "#64748b",
                    bgcolor: rechargeNote === n ? "#eff6ff" : "transparent",
                  }}
                />
              ))}
            </Stack>
          </Box>
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2, pt: 1, gap: 1 }}>
          <Button
            onClick={() => setRechargeOpen(false)}
            sx={{
              textTransform: "none",
              color: "#475569",
              fontWeight: 700,
              fontSize: "0.85rem",
              borderRadius: 2,
              px: 2,
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleRecharge}
            disabled={rechargeLoading || !rechargeAmount || !rechargeUserId}
            startIcon={rechargeLoading ? <CircularProgress size={16} color="inherit" /> : <AddCardIcon sx={{ fontSize: "16px !important" }} />}
            sx={{
              textTransform: "none",
              borderRadius: 2,
              px: 2.8,
              py: 0.7,
              fontSize: "0.85rem",
              fontWeight: 800,
              color: "#ffffff !important",
              background: "linear-gradient(135deg, #16a34a 0%, #15803d 100%)",
              boxShadow: "0 3px 10px rgba(22, 163, 74, 0.25)",
              "&:hover": {
                background: "linear-gradient(135deg, #15803d 0%, #166534 100%)",
              },
              "&.Mui-disabled": {
                color: "rgba(255, 255, 255, 0.7) !important",
                background: "#94a3b8",
              },
            }}
          >
            Recharge
          </Button>
        </DialogActions>
      </Dialog>

      {/* ================= LEDGER DRAWER ================= */}
      <Drawer
        anchor="right"
        open={ledgerOpen}
        onClose={() => setLedgerOpen(false)}
        PaperProps={{
          sx: {
            width: { xs: "100vw", sm: 380 },
            p: 0,
            bgcolor: isDark ? "background.default" : "#f8fafc",
          },
        }}
      >
        {/* Dense Drawer Header */}
        <Box
          sx={{
            px: 2,
            py: 1.2,
            bgcolor: isDark ? "background.paper" : "#ffffff",
            borderBottom: "1px solid",
            borderColor: isDark ? "rgba(51, 65, 85, 0.7)" : "#e2e8f0",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Avatar
              sx={{
                bgcolor: "#4f46e5",
                fontWeight: 700,
                width: 30,
                height: 30,
                fontSize: "0.78rem",
              }}
            >
              {(ledgerMember?.firstname?.[0] ?? "?").toUpperCase()}
            </Avatar>
            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 800, color: "text.primary", fontSize: "0.88rem", lineHeight: 1.2 }}>
                {ledgerMember ? toTitleCase(ledgerMember.firstname + " " + (ledgerMember.lastname ?? "")) : ""}'s Ledger
              </Typography>
              <Typography variant="caption" sx={{ color: "text.secondary", fontSize: "0.68rem" }}>
                Statement & transaction timeline
              </Typography>
            </Box>
          </Box>
          <IconButton onClick={() => setLedgerOpen(false)} size="small" sx={{ width: 26, height: 26, bgcolor: isDark ? "rgba(255, 255, 255, 0.06)" : "#f1f5f9" }}>
            <CloseIcon sx={{ fontSize: 15 }} />
          </IconButton>
        </Box>

        {/* Dense Balance Hero Card */}
        <Box sx={{ px: 2, pt: 1.5, pb: 0.5 }}>
          <Box
            sx={{
              p: 1.5,
              borderRadius: 2.5,
              background:
                ledgerBalance < 0
                  ? "linear-gradient(135deg, #ef4444 0%, #b91c1c 100%)"
                  : ledgerBalance > 0
                    ? "linear-gradient(135deg, #10b981 0%, #047857 100%)"
                    : "linear-gradient(135deg, #334155 0%, #1e293b 100%)",
              color: "#ffffff",
              boxShadow: "0 4px 12px -2px rgba(0, 0, 0, 0.15)",
            }}
          >
            <Typography variant="caption" sx={{ textTransform: "uppercase", opacity: 0.85, fontWeight: 700, fontSize: "0.62rem", letterSpacing: 0.4 }}>
              Current Net Balance
            </Typography>
            <Typography variant="h5" sx={{ fontWeight: 900, my: 0.2, fontSize: "1.4rem", fontFamily: "monospace" }}>
              {ledgerBalance < 0 ? "- ₹" : "₹"}
              {Math.abs(parseFloat(ledgerBalance)).toLocaleString("en-IN", {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              })}
            </Typography>
            <Typography variant="caption" sx={{ opacity: 0.85, fontSize: "0.68rem", display: "block" }}>
              {ledgerBalance < 0
                ? "⚠️ Deficit: Spent from own pocket"
                : ledgerBalance > 0
                  ? "✓ Available credit in home wallet"
                  : "Balanced: No advance or deficit"}
            </Typography>

            {/* Quick summary strip */}
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                mt: 1,
                pt: 0.8,
                borderTop: "1px solid rgba(255, 255, 255, 0.2)",
              }}
            >
              <Box>
                <Typography variant="caption" sx={{ opacity: 0.75, display: "block", fontSize: "0.62rem" }}>
                  TOTAL RECHARGED
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 800, fontSize: "0.82rem" }}>
                  +₹{totalRecharged.toFixed(2)}
                </Typography>
              </Box>
              <Box sx={{ textAlign: "right" }}>
                <Typography variant="caption" sx={{ opacity: 0.75, display: "block", fontSize: "0.62rem" }}>
                  TOTAL AUTO-DEDUCTED
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 800, fontSize: "0.82rem" }}>
                  -₹{totalSpent.toFixed(2)}
                </Typography>
              </Box>
            </Box>
          </Box>
        </Box>

        {/* Ledger Transaction Stream */}
        <Box sx={{ overflowY: "auto", flex: 1, px: 2, py: 1 }}>
          <Typography variant="caption" sx={{ fontWeight: 800, color: "text.secondary", fontSize: "0.72rem", textTransform: "uppercase", letterSpacing: 0.4, display: "block", mb: 0.8 }}>
            History ({ledgerData.length})
          </Typography>

          {ledgerLoading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
              <CircularProgress size={24} />
            </Box>
          ) : ledgerData.length === 0 ? (
            <Box
              sx={{
                textAlign: "center",
                py: 4,
                bgcolor: isDark ? "rgba(255, 255, 255, 0.02)" : "#ffffff",
                borderRadius: 2,
                border: "1px dashed",
                borderColor: isDark ? "rgba(51, 65, 85, 0.7)" : "#cbd5e1",
              }}
            >
              <ReceiptLongIcon sx={{ fontSize: 32, color: "text.secondary", mb: 0.5 }} />
              <Typography variant="caption" sx={{ color: "text.secondary", fontWeight: 600, display: "block" }}>
                No wallet transactions recorded yet.
              </Typography>
            </Box>
          ) : (
            <Stack spacing={0.8}>
              {ledgerData
                .slice()
                .reverse()
                .map((entry) => {
                  const isRecharge = entry.type === "recharge";
                  return (
                    <Paper
                      key={entry.id}
                      elevation={0}
                      sx={{
                        p: 1,
                        borderRadius: 2,
                        bgcolor: isDark ? "background.paper" : "#ffffff",
                        border: "1px solid",
                        borderColor: isDark ? "rgba(51, 65, 85, 0.7)" : "#e2e8f0",
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        transition: "all 0.15s ease",
                        "&:hover": {
                          borderColor: isRecharge ? "#86efac" : "#fca5a5",
                          boxShadow: "0 2px 6px rgba(0,0,0,0.03)",
                        },
                      }}
                    >
                      {/* Icon */}
                      <Box
                        sx={{
                          width: 28,
                          height: 28,
                          borderRadius: 1.8,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          flexShrink: 0,
                          bgcolor: isDark
                            ? isRecharge
                              ? "rgba(34, 197, 94, 0.18)"
                              : "rgba(239, 68, 68, 0.18)"
                            : isRecharge
                              ? "#dcfce7"
                              : "#fee2e2",
                          color: isRecharge
                            ? isDark ? "#4ade80" : "#16a34a"
                            : isDark ? "#f87171" : "#dc2626",
                        }}
                      >
                        {isRecharge ? (
                          <ArrowUpwardIcon sx={{ fontSize: 15 }} />
                        ) : (
                          <ShoppingBagIcon sx={{ fontSize: 15 }} />
                        )}
                      </Box>

                      {/* Content */}
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 0.8 }}>
                          <Typography variant="body2" sx={{ fontWeight: 700, color: "text.primary", fontSize: "0.78rem" }} noWrap>
                            {entry.note || (isRecharge ? "Recharge" : "Home Expense")}
                          </Typography>
                          <Typography
                            variant="body2"
                            sx={{
                              fontWeight: 800,
                              color: isRecharge
                                ? isDark ? "#4ade80" : "#16a34a"
                                : isDark ? "#f87171" : "#dc2626",
                              fontFamily: "monospace",
                              fontSize: "0.82rem",
                              flexShrink: 0,
                            }}
                          >
                            {isRecharge ? "+" : "-"}₹{entry.amount.toFixed(2)}
                          </Typography>
                        </Box>

                        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mt: 0.2 }}>
                          <Typography variant="caption" sx={{ color: "text.secondary", fontSize: "0.65rem" }}>
                            {formatDate(entry.created_at)}
                            {entry.performed_by_name && ` • by ${toTitleCase(entry.performed_by_name)}`}
                          </Typography>

                          <Typography
                            variant="caption"
                            sx={{
                              fontWeight: 700,
                              color: isDark ? "#94a3b8" : "#475569",
                              bgcolor: isDark ? "rgba(255, 255, 255, 0.06)" : "#f1f5f9",
                              px: 0.6,
                              py: 0.1,
                              borderRadius: 1,
                              fontSize: "0.62rem",
                            }}
                          >
                            Bal: ₹{entry.running_balance.toFixed(2)}
                          </Typography>
                        </Box>
                      </Box>
                    </Paper>
                  );
                })}
            </Stack>
          )}
        </Box>
      </Drawer>
    </Box>
  );
};

export default DI(React.memo(WalletPanel));
